const COURT_PATTERN = /\b(?:cour(?:t|y)|vcat|neighbourhood justice centre)\b/i;
const DAY_PATTERN = /^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s+\d{1,2}\b/i;
const LISTED_LAWYERS = new Set([
  "JO", "DE", "DL", "LT", "MM", "LF", "JV", "GJ", "PR", "BM", "CMR",
  "TS", "DP", "ET", "HLB", "RG", "JM", "SHO", "XF", "TWB", "PC", "CS",
  "GW", "SP", "CM", "HB", "NN", "CC", "JB", "NM", "AM", "BN",
]);

function cleanText(value = "") {
  return String(value || "")
    .replace(/[\u2022\u00b7]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function joinItems(items = []) {
  return cleanText(items.sort((a, b) => a.x - b.x).map((item) => item.str).join(" "));
}

export function groupValsDiaryPageLines(page = {}) {
  const width = Number(page.width) || 595;
  const divider = width / 2;
  const items = (page.items || [])
    .filter((item) => String(item?.str || "").trim())
    .map((item) => ({
      str: String(item.str),
      x: Number(item.x ?? item.transform?.[4] ?? 0),
      y: Number(item.y ?? item.transform?.[5] ?? 0),
      fontName: String(item.fontName || ""),
    }))
    .sort((a, b) => (b.y - a.y) || (a.x - b.x));

  const lines = [];
  for (const item of items) {
    let line = lines.find((candidate) => Math.abs(candidate.y - item.y) <= 1.6);
    if (!line) {
      line = { y: item.y, items: [] };
      lines.push(line);
    }
    line.items.push(item);
  }

  return lines
    .sort((a, b) => b.y - a.y)
    .map((line) => {
      const leftItems = line.items.filter((item) => item.x < divider);
      const rightItems = line.items.filter((item) => item.x >= divider);
      return {
        ...line,
        leftItems,
        rightItems,
        leftText: joinItems(leftItems),
        rightText: joinItems(rightItems),
      };
    });
}

function parseClient(raw = "") {
  const value = cleanText(raw).replace(/^[-–—]\s*/, "");
  const bracket = value.match(/^(.*?)\s*\(([^)]+)\)\s*(.*)$/);
  if (bracket) {
    const remainder = cleanText(bracket[3]);
    const tokens = remainder.match(/\b[A-Z]{2,4}\b/g) || [];
    const lawyer = tokens.find((token) => LISTED_LAWYERS.has(token)) || "";
    const notes = cleanText(remainder.replace(new RegExp(`^${lawyer}\\b\\s*`, "i"), ""));
    return {
      client_name: cleanText(bracket[1]),
      appearance_type: cleanText(bracket[2]),
      lawyer_initials: lawyer,
      notes,
    };
  }

  const parts = value.split(/\s+[–—-]\s+/);
  return {
    client_name: cleanText(parts.shift()),
    appearance_type: "",
    lawyer_initials: "",
    notes: cleanText(parts.join(" - ")),
  };
}

function clientSurnames(clientName = "") {
  const uppercase = String(clientName).match(/\b[A-Z][A-Z'’-]{2,}\b/g) || [];
  if (uppercase.length) return uppercase;
  const words = cleanText(clientName).split(" ");
  return words.length ? [words[words.length - 1].toUpperCase()] : [];
}

function requiresAppearanceConfirmation(value = "") {
  const text = cleanText(value);
  return /\b(?:advise|confirm)\b/i.test(text)
    && /\b(?:appear(?:ing)?|adj(?:ourn(?:ing|ment)?)?|brief(?:ing|ed)?)\b/i.test(text);
}

function parseAppearanceLine(line, italicFonts) {
  const text = cleanText(line.rightText);
  if (!text || /^(SOLICITOR|Solicitor)$/i.test(text)) return null;
  const isItalic = line.rightItems.some((item) => italicFonts.has(item.fontName));
  if (requiresAppearanceConfirmation(text)) {
    return { appearing: "TBC", targets: "", isBarrister: false, source: text, y: line.y };
  }
  const briefed = text.match(/^(.+?)\s+briefed\s+for\s+(.+)$/i);
  if (briefed) {
    const appearing = cleanText(briefed[1]).replace(/\s+has\s+been$/i, "");
    return { appearing, targets: cleanText(briefed[2]), isBarrister: true, source: text, y: line.y };
  }
  const parentheticalBriefed = text.match(/^[A-Z]{2,4}\s*\((.+?)\s+briefed\)$/i);
  if (parentheticalBriefed) {
    return { appearing: cleanText(parentheticalBriefed[1]), targets: "", isBarrister: true, source: text, y: line.y };
  }
  const appearing = text.match(/^([A-Z]{2,4})\s+appearing\s+for\s+(.+)$/i);
  if (appearing) {
    return { appearing: appearing[1].toUpperCase(), targets: cleanText(appearing[2]), isBarrister: isItalic, source: text, y: line.y };
  }
  const advisedFor = text.match(/^([A-Z]{2,4})\b.*\bfor\s+(.+)$/i);
  if (advisedFor && LISTED_LAWYERS.has(advisedFor[1].toUpperCase())) {
    return { appearing: advisedFor[1].toUpperCase(), targets: cleanText(advisedFor[2]), isBarrister: isItalic, source: text, y: line.y };
  }
  const initials = text.match(/^([A-Z]{2,4})(?:\b|$)/);
  if (initials && (LISTED_LAWYERS.has(initials[1]) || text === initials[1])) {
    return { appearing: initials[1], targets: "", isBarrister: isItalic, source: text, y: line.y };
  }
  if (isItalic) {
    return { appearing: text, targets: "", isBarrister: true, source: text, y: line.y };
  }
  return null;
}

function buildCourtEntries(block, date, section, italicFonts) {
  if (!block?.court || !block.clients.length) return [];
  const appearances = block.rightLines.map((line) => parseAppearanceLine(line, italicFonts)).filter(Boolean);
  const blockInstruction = block.rightLines.map((line) => cleanText(line.rightText)).filter(Boolean).join(" ");
  const confirmationDefault = requiresAppearanceConfirmation(blockInstruction)
    ? { appearing: "TBC", targets: "", isBarrister: false, source: blockInstruction, y: null }
    : null;
  const targeted = appearances.filter((appearance) => appearance.targets);
  const defaults = appearances.filter((appearance) => !appearance.targets);

  return block.clients.map((clientRow) => {
    const client = parseClient(clientRow.raw);
    const surnames = clientSurnames(client.client_name);
    const targetMatch = targeted.find((appearance) => surnames.some((surname) =>
      new RegExp(`\\b${surname.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i").test(appearance.targets)
    ));
    const alignedMatch = appearances.find((appearance) => Math.abs(appearance.y - clientRow.y) <= 2);
    const match = targetMatch
      || confirmationDefault
      || alignedMatch
      || defaults[0]
      || (block.clients.length === 1 ? targeted[0] : null);

    return {
      date,
      court: cleanText(block.court),
      diary_section: section,
      client_name: client.client_name,
      appearance_type: client.appearance_type,
      lawyer_initials: client.lawyer_initials,
      appearing: match?.appearing || "",
      appearing_is_barrister: Boolean(match?.isBarrister),
      counsel_briefed: match?.isBarrister ? match.source : "",
      outcome: client.notes,
      notes: "",
      grant_type: "",
      claimable: false,
      next_date: "",
      atlas_claim_type: "",
    };
  });
}

export function parseValsDiaryPages(pages = []) {
  const entries = [];
  let currentDate = "";
  let currentSection = "Crime Team Diary";
  let block = null;
  let currentItalicFonts = new Set();

  const flush = () => {
    if (block) entries.push(...buildCourtEntries(block, currentDate, currentSection, currentItalicFonts));
    block = null;
  };

  for (const page of pages) {
    const pageLines = groupValsDiaryPageLines(page);
    currentItalicFonts = new Set(
      (page.items || [])
        .filter((item) => {
          const text = String(item.str || "");
          return /\bbriefed\s+for\b/i.test(text) && !/\b(?:advise|confirm)\b/i.test(text);
        })
        .map((item) => String(item.fontName || ""))
        .filter(Boolean)
    );
    const courtFonts = new Set(
      pageLines
        .filter((line) => COURT_PATTERN.test(line.leftText))
        .flatMap((line) => line.leftItems.map((item) => item.fontName))
        .filter(Boolean)
    );
    for (const line of pageLines) {
      const left = cleanText(line.leftText);
      const right = cleanText(line.rightText);
      if (!left && !right) continue;

      const dayMatch = left.match(DAY_PATTERN);
      if (dayMatch) {
        flush();
        currentDate = cleanText(dayMatch[0]);
        currentSection = "Crime Team Diary";
        continue;
      }
      if (/BALIT\s+NGULU\s+DIARY/i.test(left)) {
        flush();
        currentSection = "BALIT NGULU DIARY";
        continue;
      }
      if (/CRIME\s+TEAM\s+DIARY/i.test(left)) {
        flush();
        currentSection = "Crime Team Diary";
        continue;
      }
      if (/^(COURT|Court)$/i.test(left) || /^(SOLICITOR|Solicitor)$/i.test(right)) continue;

      const usesCourtFont = line.leftItems.length > 0 && line.leftItems.every((item) => courtFonts.has(item.fontName));
      const isCourtLine = COURT_PATTERN.test(left) || (usesCourtFont && /^[A-Z]/.test(left));
      if (isCourtLine && block && block.clients.length === 0 && !COURT_PATTERN.test(left)) {
        block.court = `${block.court} ${left}`;
        if (right) block.rightLines.push(line);
        continue;
      }
      if (isCourtLine) {
        flush();
        block = { court: left, clients: [], rightLines: right ? [line] : [] };
        continue;
      }
      if (!block) continue;

      const hasBullet = line.leftItems.some((item) => /[\u2022\u00b7]/.test(item.str));
      if (hasBullet) {
        block.clients.push({ raw: left, y: line.y });
      } else if (left && /^\([^)]*\)$/.test(left) && block.clients.length === 0) {
        block.court = `${block.court} ${left}`;
      } else if (left && block.clients.length) {
        const lastClient = block.clients[block.clients.length - 1];
        lastClient.raw = `${lastClient.raw} ${left}`;
      }
      if (right) block.rightLines.push(line);
    }
    flush();
  }

  return entries.filter((entry) => entry.client_name);
}
